import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BiSearch } from 'react-icons/bi';
import { TbLogout, TbDeviceLaptop, TbSchool } from 'react-icons/tb';
import { BsBuilding } from 'react-icons/bs';
import styles from './Header.module.css';
import logo from '../../assets/thuto_mdm-logo.svg';
import avatar from '../../assets/mdm-avatar.jpg';
import { useAuth } from '../../context/AuthContext';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';

const TYPE_LABEL = { district: 'District', device: 'Device', school: 'School' };
const TYPE_ICON  = {
  district: <BsBuilding size={13} />,
  device:   <TbDeviceLaptop size={13} />,
  school:   <TbSchool size={13} />,
};

function Header({ breadcrumbs = [] }) {
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeIdx,   setActiveIdx]   = useState(-1);

  const menuRef   = useRef(null);
  const searchRef = useRef(null);
  const { auth, logout } = useAuth();
  const navigate = useNavigate();

  const {
    query, setQuery,
    results, open, setOpen,
    onFocus, searching, navigating,
    handleSelect,
  } = useGlobalSearch(navigate);

  // Close avatar menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close search dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen]);

  // Reset active index when results change
  useEffect(() => { setActiveIdx(-1); }, [results]);

  function handleKeyDown(e) {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && results[activeIdx]) handleSelect(results[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const showDropdown = open && (query.trim() || searching);
  const hasResults   = results.length > 0;

  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <Link to="/" className={styles.brand}>
          <img src={logo} alt="Thuto MDM" className={styles.logo} />
        </Link>
        <nav className={styles.breadcrumb}>
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className={styles.crumbItem}>
              {i > 0 && <span className={styles.crumbSep}>›</span>}
              {crumb.path
                ? <Link to={crumb.path} className={styles.crumbLink}>{crumb.label}</Link>
                : <span className={styles.crumbCurrent}>{crumb.label}</span>
              }
            </span>
          ))}
        </nav>
      </div>

      <div className={styles.center}>
        <div className={styles.searchWrapper} ref={searchRef}>
          <div className={`${styles.search} ${open ? styles.searchFocused : ''}`}>
            <BiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search districts, IMEI, EMIS…"
              className={styles.searchInput}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={onFocus}
              onKeyDown={handleKeyDown}
              autoComplete="off"
            />
            {navigating && <span className={styles.searchSpinner} />}
          </div>

          {showDropdown && (
            <div className={styles.dropdown}>
              {searching && !hasResults && (
                <div className={styles.dropdownEmpty}>Searching…</div>
              )}
              {!searching && !hasResults && query.trim() && (
                <div className={styles.dropdownEmpty}>No results for "{query.trim()}"</div>
              )}
              {hasResults && results.map((result, i) => (
                <button
                  key={`${result.type}-${result.label}`}
                  className={`${styles.dropdownResult} ${i === activeIdx ? styles.dropdownResultActive : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(result); }}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  <span className={styles.resultIcon}>{TYPE_ICON[result.type]}</span>
                  <span className={styles.resultLabel}>{result.label}</span>
                  <span className={`${styles.resultType} ${styles[`resultType_${result.type}`]}`}>
                    {TYPE_LABEL[result.type]}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.right}>
        <div className={styles.avatarWrapper} ref={menuRef}>
          <img
            src={avatar}
            alt="User avatar"
            className={styles.avatar}
            onClick={() => setMenuOpen((o) => !o)}
          />
          {menuOpen && (
            <div className={styles.menu}>
              <div className={styles.menuUser}>
                <img src={avatar} alt="" className={styles.menuAvatar} />
                <div>
                  <div className={styles.menuName}>{auth?.user?.fullName || 'User'}</div>
                  <div className={styles.menuUsername}>@{auth?.user?.username || ''}</div>
                </div>
              </div>
              <div className={styles.menuDivider} />
              <button className={styles.menuLogout} onClick={() => { setMenuOpen(false); setConfirmOpen(true); }}>
                <TbLogout size={16} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      {confirmOpen && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <div className={styles.modalIcon}>
              <TbLogout size={28} />
            </div>
            <h2 className={styles.modalTitle}>Sign out</h2>
            <p className={styles.modalBody}>Are you sure you want to sign out of your account?</p>
            <div className={styles.modalActions}>
              <button className={styles.modalCancel} onClick={() => setConfirmOpen(false)}>
                Cancel
              </button>
              <button className={styles.modalConfirm} onClick={handleLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
